// Assignments v2 · the lists' pure helpers (24 Oct 2026): "Needs my action" grouped by what is asked (decisions first), which rows decide
// inline and in a batch (a gate's review never: it has checks to tick on its page), the marks of a row (on hold, at risk, acting for, the
// owner acting for someone, the brief changed), the viewer's filters (type, priority, due window, department) read back from storage, the
// portfolio's health (tone, the chat card's health carried in `step`, the local filter) and its CSV export, and the glance of the morning
// summary (My workspace's entry). No React here: ./lists.test.mjs bundles it with Vite's esbuild.
import {
  dateValue, localText, personName, type AssignmentListItem, type BatchDecideResult, type MorningSummary, type MyAction, type PortfolioHealth,
  type PortfolioItem, type Priority,
} from "../model";

type Lang = "en" | "ar";

// ------------------------------------------------------------------------------------------------------------- needs my action

/** What is asked, as the list groups it: decisions (review · gate · final · acknowledge), new work to accept, returns to revise, date
 *  requests, questions to answer, a brief that changed, and declines to reassign. */
export type ActionGroup = "decide" | "accept" | "revise" | "dates" | "questions" | "brief" | "declined";

export const ACTION_GROUPS: readonly ActionGroup[] = ["decide", "accept", "revise", "dates", "questions", "brief", "declined"];

const GROUP_OF: Record<MyAction, ActionGroup> = {
  review: "decide", gate: "decide", final: "decide", acknowledge: "decide",
  accept: "accept", revise: "revise", extension: "dates", answer: "questions", "brief-changed": "brief", reassign: "declined",
};

/** The rows waiting on the viewer, grouped by what is asked, in ACTION_GROUPS order (empty groups left out; each keeps the server's order). */
export function groupByAction(items: readonly AssignmentListItem[]): { key: ActionGroup; items: AssignmentListItem[] }[] {
  const by = new Map<ActionGroup, AssignmentListItem[]>();
  for (const a of items) {
    if (!a.myAction) continue;
    const g = GROUP_OF[a.myAction];
    if (!g) continue;
    by.set(g, [...(by.get(g) ?? []), a]);
  }
  return ACTION_GROUPS.filter((g) => by.has(g)).map((key) => ({ key, items: by.get(key)! }));
}

/** The decision a row takes inline and in a batch: approve (a level's review, the requester's final), acknowledge (an information request);
 *  none for a gate's review (its checks are ticked on the page) or any other action. */
export function inlineDecision(a: Pick<AssignmentListItem, "myAction">): "approve" | "acknowledge" | null {
  return a.myAction === "review" || a.myAction === "final" ? "approve" : a.myAction === "acknowledge" ? "acknowledge" : null;
}

export const batchable = (a: Pick<AssignmentListItem, "myAction">): boolean => inlineDecision(a) !== null;

/** The ids still selected that may go in a batch (a row decided meanwhile, or no longer listed, drops out). */
export function selectedBatch(items: readonly AssignmentListItem[], selected: ReadonlySet<string>): AssignmentListItem[] {
  return items.filter((a) => selected.has(a.id) && batchable(a));
}

/** How a batch went: how many went through, and the ones refused (each with the server's reason). */
export function batchOutcome(results: readonly BatchDecideResult[]): { ok: number; failed: BatchDecideResult[] } {
  return { ok: results.filter((r) => r.ok).length, failed: results.filter((r) => !r.ok) };
}

/** A refused item's reason in the reader's language. */
export const resultReason = (r: Pick<BatchDecideResult, "error" | "errorAr">, lang: Lang): string => (lang === "ar" && r.errorAr) || r.error || "";

// ------------------------------------------------------------------------------------------------------------- a row's marks

export type RowMark = "hold" | "atRisk" | "actingFor" | "ownerActing" | "briefChanged";

/** The marks a row carries besides its step, in reading order. At risk gives way to overdue (the days already say it in red); the owner
 *  acting for someone shows here only while the next step is with someone else ("With Sultan · acting for Reem" says it otherwise); a
 *  portfolio row's health is not said twice (on hold, at risk, late). */
export function rowMarks(a: Pick<AssignmentListItem, "onHold" | "atRisk" | "overdue" | "actingFor" | "ownerActingFor" | "briefChanged" | "status" | "owner" | "holder">,
  health?: PortfolioHealth | null): RowMark[] {
  const open = a.status !== "approved" && a.status !== "cancelled";
  const ownerHolds = !!a.holder && a.holder.id.toLowerCase() === a.owner.id.toLowerCase();
  const out: RowMark[] = [];
  if (a.onHold && open && health !== "on-hold") out.push("hold");
  if (a.atRisk && !a.overdue && open && health !== "at-risk" && health !== "late") out.push("atRisk");
  if (a.actingFor) out.push("actingFor");
  if (a.ownerActingFor && open && !ownerHolds) out.push("ownerActing");
  if (a.briefChanged && open) out.push("briefChanged");
  return out;
}

// ------------------------------------------------------------------------------------------------------------- filters

export type DueWindow = "overdue" | "week" | "later" | "none";
export const DUE_WINDOWS: readonly DueWindow[] = ["overdue", "week", "later", "none"];
const PRIORITY_VALUES: readonly Priority[] = ["normal", "high", "urgent"];

/** The viewer's filters over the loaded rows; an absent key lets every row through. */
export interface ListFilter {
  type?: string;
  priority?: Priority;
  due?: DueWindow;
  department?: string;
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** The due window of a row: overdue (as the server counts it: open, past due, not on hold), within a week (or past, once closed or held),
 *  later, or no date. */
export function dueWindow(a: Pick<AssignmentListItem, "dueAt" | "overdue">, now: number = Date.now()): DueWindow {
  if (!a.dueAt) return "none";
  if (a.overdue) return "overdue";
  const t = new Date(a.dueAt).getTime();
  if (Number.isNaN(t)) return "none";
  return t <= now + WEEK_MS ? "week" : "later";
}

export function applyFilter<T extends Pick<AssignmentListItem, "typeKey" | "priority" | "dueAt" | "overdue" | "departmentKey">>(
  items: readonly T[], f: ListFilter, now: number = Date.now(),
): T[] {
  return items.filter((a) =>
    (!f.type || a.typeKey === f.type)
    && (!f.priority || a.priority === f.priority)
    && (!f.due || dueWindow(a, now) === f.due)
    && (!f.department || a.departmentKey === f.department));
}

/** How many filters are set (the toolbar's "Clear"). */
export const filterCount = (f: ListFilter): number => [f.type, f.priority, f.due, f.department].filter(Boolean).length;

/** What the rows offer to filter by: the types (key and name, by name), the departments (keys) and the priorities present. */
export function filterOptions(items: readonly AssignmentListItem[], lang: Lang): { types: { key: string; name: string }[]; departments: string[]; priorities: Priority[] } {
  const types = new Map<string, string>();
  const departments = new Set<string>();
  const priorities = new Set<Priority>();
  for (const a of items) {
    if (a.typeKey) types.set(a.typeKey, localText(a.typeName, lang) || a.typeKey);
    if (a.departmentKey) departments.add(a.departmentKey);
    priorities.add(a.priority);
  }
  return {
    types: [...types].map(([key, name]) => ({ key, name })).sort((x, y) => x.name.localeCompare(y.name, lang)),
    departments: [...departments].sort(),
    priorities: PRIORITY_VALUES.filter((p) => priorities.has(p)),
  };
}

/** The filters kept for a viewer (stored JSON), with anything unknown dropped; {} when absent or unreadable. */
export function parseFilter(raw: string | null | undefined): ListFilter {
  if (!raw) return {};
  let v: unknown;
  try { v = JSON.parse(raw); } catch { return {}; }
  if (!v || typeof v !== "object" || Array.isArray(v)) return {};
  const o = v as Record<string, unknown>;
  const text = (x: unknown) => (typeof x === "string" && x.trim() && x.length <= 120 ? x : undefined);
  const out: ListFilter = {};
  if (text(o.type)) out.type = o.type as string;
  if (PRIORITY_VALUES.includes(o.priority as Priority)) out.priority = o.priority as Priority;
  if (DUE_WINDOWS.includes(o.due as DueWindow)) out.due = o.due as DueWindow;
  if (text(o.department)) out.department = o.department as string;
  return out;
}

/** The storage key of a viewer's filters (per person, on this browser). */
export const filterStorageKey = (userId: string | null | undefined): string => `econic.assignments.filters.${userId ?? "anon"}`;

// ------------------------------------------------------------------------------------------------------------- the portfolio

/** The health a chat card's portfolio row carries in `step` (on track keeps the real step). */
const CARRIED: readonly string[] = ["waiting-on-me", "late", "at-risk", "on-hold"];
export const isCarriedHealth = (step: string | null | undefined): step is PortfolioHealth => !!step && CARRIED.includes(step);

export const HEALTHS: readonly PortfolioHealth[] = ["waiting-on-me", "late", "at-risk", "on-hold", "on-track", "done"];

/** The tone of a health (tokens only: the looks colour them). */
export function healthTone(h: PortfolioHealth): "info" | "crit" | "warn" | "neutral" | "good" {
  switch (h) {
    case "waiting-on-me": return "info";
    case "late": return "crit";
    case "at-risk": return "warn";
    case "on-track":
    case "done": return "good";
    default: return "neutral";
  }
}

/** The portfolio's counts, every health present (the server sends none when nothing was asked for). */
export function healthCounts(counts: Partial<Record<PortfolioHealth, number>> | null | undefined): Record<PortfolioHealth, number> {
  return Object.fromEntries(HEALTHS.map((h) => [h, Math.max(0, Number(counts?.[h] ?? 0) || 0)])) as Record<PortfolioHealth, number>;
}

export interface PortfolioFilter {
  health?: PortfolioHealth;
  department?: string;
  type?: string;
}

export function filterPortfolio(items: readonly PortfolioItem[], f: PortfolioFilter): PortfolioItem[] {
  return items.filter((p) =>
    (!f.health || p.health === f.health)
    && (f.department === undefined || (p.item.departmentKey ?? "") === f.department)
    && (f.type === undefined || (p.item.typeKey ?? "") === f.type));
}

/** One spreadsheet cell: quoted when it holds a comma, a quote or a line break; a leading = + - @ is neutralised (no formula runs). */
export function csvCell(value: string | number | null | undefined): string {
  let s = value === null || value === undefined ? "" : String(value);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) || s !== s.trim() ? `"${s.replace(/"/g, '""')}"` : s;
}

export const toCsv = (rows: readonly (readonly (string | number | null | undefined)[])[]): string => rows.map((r) => r.map(csvCell).join(",")).join("\r\n");

/** The portfolio's rows for the export: reference, title, type, department, holder, due (yyyy-mm-dd), health, progress — in the reader's language. */
export function portfolioRows(items: readonly PortfolioItem[], lang: Lang, healthLabel: (h: PortfolioHealth) => string): string[][] {
  return items.map((p) => {
    const a = p.item;
    return [
      a.reference,
      (lang === "ar" && a.titleAr) || a.title,
      localText(a.typeName, lang),
      (lang === "ar" && p.departmentNameAr) || p.departmentName || "",
      a.status === "approved" || a.status === "cancelled" ? "" : personName(a.holder, lang),
      dateValue(a.dueAt),
      healthLabel(p.health),
      a.progress ?? "",
    ];
  });
}

/** The export's file name: assignments-portfolio-2026-10-24.csv. */
export const csvFileName = (now: Date = new Date()): string => `assignments-portfolio-${dateValue(now.toISOString())}.csv`;

// ------------------------------------------------------------------------------------------------------------- the morning glance

/** My workspace's short line: what waits on the person, what is late, what is at risk — the first two that are not zero. */
export function glance(m: Pick<MorningSummary, "waitingOnMe" | "late" | "atRisk"> | null | undefined): { key: "waiting" | "late" | "atRisk"; count: number }[] {
  if (!m) return [];
  return ([["waiting", m.waitingOnMe], ["late", m.late], ["atRisk", m.atRisk]] as const)
    .filter(([, n]) => n > 0).slice(0, 2).map(([key, count]) => ({ key, count }));
}
